import React, { memo, useCallback, useState } from 'react'

import { Search as SearchIcon } from '@mui/icons-material'
import {
  Fade,
  InputAdornment,
  ListItem,
  Popover,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import {
  Field,
  getIn,
  setIn,
  useFormikContext,
} from 'formik'

import createLogger from '~/src/Lib/Logging'
import { defer, EMPTY_OBJECT } from '~/src/Lib/Utils'
import Button from '~/src/UI/Shared/Button'
import TextField from '~/src/UI/Shared/Form/TextField'

import { useAssigneeOptions } from './utils'

const displayName = 'Task/shared/AddStaffPopover'

const logger = createLogger(displayName)

export const styles = theme => ({
  root: {},
  addStaffButton: {
    '&:hover': {
      backgroundColor: 'transparent !important',
      color: `${theme.palette.primary.lighter} !important`
    }
  },
  searchField: {
    height: 44,
    padding: theme.spacing(0.75, 1.25),
    '& [class*=MuiInput-root]': {
      height: 32,
      minHeight: 32,
      padding: theme.spacing(0, 2, 0, 0),
      marginTop: 0,
      background: theme.palette.appPrimary.enabled,
      borderTopRightRadius: 6,
      borderTopLeftRadius: 6
    },
    '& [class*=MuiFormLabel-root]': {
      fontSize: 12,
      top: -10,
      left: 48,
      color: `${theme.palette.contrast.disabled} !important`,
    },
    '& [class*=MuiInputAdornment-positionStart]': {
      marginLeft: 0,
      paddingLeft: 3
    }
  },
  assigneesList: {
    fontSize: 16,
    height: 44,
    padding: theme.spacing(1.5, 4)
  }
})
const useStyles = makeStyles(styles)

const popoverAnchorOrigin = { vertical: 'bottom', horizontal: 'left' }
const popoverSlotProps = {
  paper: {
    id: 'add-staff-popover',
    style: {
      maxHeight: '30%',
      top: 575
    }
  }
}
const popoverTransformOrigin = { vertical: 'top', horizontal: 'left' }

const AddStaffPopover = memo(({
  addStaffLabel = false,
  assignees,
  disabled = false,
}) => {
  const classes = useStyles()
  const { values, setValues } = useFormikContext()
  const [addStaffAnchor, setAddStaffAnchor] = useState(null)
  const [searchStaff, setSearchStaff] = useState('')
  const staffOptions = useAssigneeOptions(values.assignees, searchStaff)
  const open = Boolean(addStaffAnchor)

  const handleAddStaffClick = useCallback(event => {
    setAddStaffAnchor(event.currentTarget)
  }, [setAddStaffAnchor])
  const handleAddStaffClose = useCallback(() => {
    setAddStaffAnchor(null)
  }, [setAddStaffAnchor])

  return (
    <>
      <Button
        aria-controls={popoverSlotProps.paper.id}
        aria-haspopup="true"
        aria-label={!assignees?.length ? 'Add staff button' : 'Assigned'}
        className={classes.addStaffButton}
        color="primary"
        disabled={disabled}
        variant="text"
        onClick={handleAddStaffClick}
      >
        {addStaffLabel || Boolean(!assignees?.length) ? '+ Add Staff' : `${assignees.length} Assigned`}
      </Button>
      <Popover
        open={open}
        anchorEl={addStaffAnchor}
        onClose={handleAddStaffClose}
        TransitionComponent={Fade}
        role="listbox"
        aria-label="Add staff popover"
        anchorOrigin={popoverAnchorOrigin}
        transformOrigin={popoverTransformOrigin}
        slotProps={popoverSlotProps}
      >
        <ListItem className={classes.searchField}>
          <TextField
            fullWidth
            size="small"
            role="option"
            name="searchStaff"
            label={searchStaff ? null : 'Search Staff'}
            value={searchStaff}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="primary" /></InputAdornment> }}
            InputLabelProps={{ shrink: false }}
            onChange={payload => setSearchStaff(payload.searchStaff)}
          />
        </ListItem>
        <Field
          name="assignees"
        >{({ field }) => {
            const onClickFactory = value => {
              defer(() => {
                setSearchStaff('')
                handleAddStaffClose()
              }, defer.priorities.highest)
              setValues(oldValues => {
                if (!oldValues) return oldValues
                const oldAssignees = getIn(oldValues, field.name) ?? EMPTY_OBJECT
                return setIn(oldValues, field.name, [...oldAssignees, { membership: value }])
              })
            }
            return (
              staffOptions.map(staffMember => (
                <ListItem
                  {...field}
                  button
                  className={classes.assigneesList}
                  name={`${staffMember.label}`}
                  role="option"
                  key={staffMember.value}
                  value={staffMember.value}
                  aria-label={staffMember.label}
                  onClick={() => onClickFactory(staffMember.value)}
                >
                  {staffMember.label}
                </ListItem>
              ))
            )
          }}
        </Field>
      </Popover>
    </>
  )
})

export default AddStaffPopover
